import { KaboodleToaster } from "components/common";
import ErrorState from "components/Error/ErrorState";
import LoadingIcon from "components/Loading/LoadingIcon";
import { CoverageList } from "components/QuerySection/components/CoverageList";
import { OCRStatusList } from "components/QuerySection/components/OCRStatusList";
import { QueryInfoCard } from "components/QuerySection/components/QueryInfoCard";
import { QuerySectionModals } from "components/QuerySection/components/QuerySectionModals";
import { useCoverageData } from "components/QuerySection/hooks/useCoverageData";
import ClaimLinkingDrawerTable from "pages/files/ClaimLinkingDrawerTable";
import LinkedClaimList from "pages/files/sections/LinkedClaimList";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useGetFileByIdQuery } from "services/file-api";
import {
  useGetLienByIdQuery,
  useGetLienFileByIdQuery,
  usePatchLienFileByIdMutation,
} from "services/lien-api";

const LienQueryPage = () => {
  const { lienFileId, lienId } = useParams<{
    lienFileId: string;
    lienId?: string;
  }>();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [patchLienFileById] = usePatchLienFileByIdMutation();

  const searchParams = new URLSearchParams(location.search);
  const queryFileId = searchParams.get("file_id");
  const fileIdToUse = queryFileId || lienFileId;

  const {
    data: lienFile,
    isLoading: isLoadingFile,
    error: lienFileError,
  } = useGetLienFileByIdQuery(
    { fileId: fileIdToUse || "" },
    { skip: !fileIdToUse },
  );

  const { file_id: fileId } = lienFile || {};

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId || "" },
    { skip: !fileId },
  );

  const { isLoading: isLoadingLien, error: lienError } = useGetLienByIdQuery(
    { lienId: lienId || "" },
    { skip: !lienId },
  );

  const {
    allCoverages,
    isLoading: isLoadingCoverage,
    hasCoverage,
    hasCoverageValidationErrors,
  } = useCoverageData(
    Number(fileId),
    fileData?.coverage_complete,
    fileData?.no_coverage,
  );

  const handleCoverageChange = useCallback(
    async (field: "coverage_complete" | "no_coverage", value: boolean) => {
      if (!fileData || !fileId) return;

      try {
        await patchLienFileById({
          fileId: fileIdToUse,
          data: { [field]: value },
        }).unwrap();

        const message =
          field === "no_coverage"
            ? value
              ? "Marked as No Coverage"
              : "No Coverage status removed"
            : value
              ? "Coverage marked as complete"
              : "Coverage marked as incomplete";

        KaboodleToaster.success(message);
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        KaboodleToaster.error(`Failed to update ${field}`);
      }
    },
    [fileData, fileId, fileIdToUse, patchLienFileById],
  );

  useEffect(() => {
    setIsLoading(isLoadingFile || (lienId ? isLoadingLien : false));
    if (lienFileError || lienError) {
      setError(new Error("An error occurred loading lien data"));
    }
  }, [isLoadingFile, isLoadingLien, lienFileError, lienError, lienId]);

  const ClaimLinking = useMemo(() => {
    return fileData?.has_section_111_claim_linkage_available ? (
      <LinkedClaimList fileData={fileData} />
    ) : null;
  }, [fileData]);

  if (isLoading) return <LoadingIcon />;
  if (error || !lienFile) return <ErrorState />;

  return (
    <>
      <div className="swim-wrapper">
        {fileId && <OCRStatusList fileId={Number(fileId)} />}
        <div className="grid-layout gl-2 no-gap-tb swimlanes msa-medical-split">
          <div>
            <div className="swim-headers">Query Information</div>
            {fileData && (
              <QueryInfoCard
                isCoverageComplete={fileData.coverage_complete}
                isNoCoverage={fileData.no_coverage}
                onCoverageChange={handleCoverageChange}
                hasCoverage={hasCoverage}
                hasCoverageValidationErrors={hasCoverageValidationErrors}
              />
            )}
            {ClaimLinking && <div className="mt-4">{ClaimLinking}</div>}
          </div>
          <div>
            <CoverageList
              allCoverages={allCoverages}
              isLoading={isLoadingCoverage}
              hasCoverage={hasCoverage}
              isCoverageComplete={fileData?.coverage_complete}
              isNoCoverage={fileData?.no_coverage}
            />
          </div>
        </div>
      </div>
      {fileData && (
        <QuerySectionModals
          fileId={fileData.id}
          organization={fileData.organization}
        />
      )}
      <ClaimLinkingDrawerTable fileData={fileData} />
    </>
  );
};

export default LienQueryPage;
